import { useState } from "react";
import { toast } from "sonner";
import { Banknote, ExternalLink, FileText, Phone } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { head, money, shopFetch } from "./shopKit";

export const PHONE = "844-527-3799";

export const SpecsTable = ({ specs }) => !specs?.length ? null : (
  <div className="overflow-hidden rounded-2xl bg-white" data-testid="shop-specs">
    <p className="border-b border-black/10 px-5 py-3 text-xs font-extrabold uppercase tracking-[2px] text-black/50" style={head}>Specifications</p>
    <dl className="divide-y divide-black/5 px-5">
      {specs.map((s) => <div key={s.label} className="flex justify-between gap-4 py-2.5 text-sm"><dt className="text-black/50">{s.label}</dt><dd className="text-right font-semibold">{s.value}</dd></div>)}
    </dl>
  </div>
);

export const MachineNote = ({ p }) => p.quote_only ? null : (
  <p className="rounded-xl bg-[#4CCC06]/10 px-4 py-3 text-sm text-black/70" data-testid="shop-machine-note">
    Ships freight for {money(p.public_shipping_usd)}. Paying by card adds a {p.card_fee_pct}% processing fee ({money((p.price_usd * p.card_fee_pct) / 100)}). Pay by bank wire to skip the fee.
  </p>
);

const Field = ({ label, k, f, setF, type = "text", required }) => (
  <label className="flex flex-col gap-1 text-xs font-semibold text-black/60">{label}{required && " *"}
    <input type={type} required={required} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`quote-${k}`}
      className="rounded-lg border border-black/15 px-3 py-2 text-sm text-black outline-none focus:border-black" />
  </label>
);

const Choice = ({ v, f, setF, icon: Icon, title, sub }) => (
  <button type="button" onClick={() => setF({ ...f, payment_preference: v })} data-testid={`quote-pref-${v}`}
    className={`flex flex-1 items-start gap-2 rounded-xl border-2 p-3 text-left transition-colors ${f.payment_preference === v ? "border-[#4CCC06] bg-[#4CCC06]/10" : "border-black/10"}`}>
    <Icon size={18} className="mt-0.5 shrink-0" /><span><b className="block text-sm">{title}</b><span className="text-xs text-black/50">{sub}</span></span>
  </button>
);

export const QuoteDialog = ({ p, open, onOpenChange, initial = "quote" }) => {
  const [f, setF] = useState({ payment_preference: initial });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { setDone(await shopFetch("/quote-request", { method: "POST", body: JSON.stringify({ ...f, product_id: p.id }) })); }
    catch (err) { toast.error(err.message); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) setDone(null); }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto bg-white text-black sm:max-w-lg" data-testid="quote-dialog">
        <DialogHeader><DialogTitle className="text-2xl font-extrabold uppercase" style={head}>{done ? "Request sent" : `${p.name}`}</DialogTitle></DialogHeader>
        {done ? (
          <div className="flex flex-col gap-3 text-sm" data-testid="quote-success">
            <p>Thanks! Reference <b>{done.ref}</b>. Our team will contact you within one business day{f.payment_preference === "wire" ? " with an invoice and bank wire instructions" : " with your quote"}.</p>
            {f.payment_preference === "wire" && <p className="text-black/50">Always confirm wire details by phone at {PHONE} before sending funds.</p>}
          </div>
        ) : (
          <form onSubmit={submit} className="flex flex-col gap-3" data-testid="quote-form">
            <div className="flex flex-col gap-2 sm:flex-row">
              <Choice v="quote" f={f} setF={setF} icon={FileText} title="Request a quote" sub="Talk it through with our team" />
              {!p.quote_only && <Choice v="wire" f={f} setF={setF} icon={Banknote} title="Pay by bank wire" sub="No card fee. We send an invoice." />}
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Name" k="name" f={f} setF={setF} required />
              <Field label="Email" k="email" type="email" f={f} setF={setF} required />
              <Field label="Phone" k="phone" type="tel" f={f} setF={setF} />
              <Field label="Company" k="company" f={f} setF={setF} />
            </div>
            <Field label="City and state" k="location" f={f} setF={setF} />
            <label className="flex flex-col gap-1 text-xs font-semibold text-black/60">Anything we should know?
              <textarea rows={3} value={f.message || ""} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="quote-message" className="rounded-lg border border-black/15 px-3 py-2 text-sm text-black outline-none focus:border-black" /></label>
            <button disabled={busy} className="rounded-full bg-black py-3 font-bold text-white transition-colors hover:bg-[#4CCC06] hover:text-black disabled:opacity-50" data-testid="quote-submit">{busy ? "Sending…" : "Send request"}</button>
            <a href={`tel:${PHONE}`} className="flex items-center justify-center gap-2 text-sm font-semibold text-black/60" data-testid="quote-call"><Phone size={14} />Or call {PHONE}</a>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

const APR = 9.99;
const TERMS = [24, 36, 48, 60];
export const LENDER_URL = "https://www.enhancify.com/alr-equipment-llc";
export const monthly = (amount, months) => { const r = APR / 100 / 12; return (amount * r) / (1 - (1 + r) ** -months); };
export const financed = (p) => p.price_usd + (p.public_shipping_usd || 0);

export const FinanceEstimate = ({ p }) => {
  const [n, setN] = useState(60);
  if (p.quote_only || !p.price_usd) return null;
  return (
    <div className="rounded-2xl border-2 border-black/10 bg-white p-5" data-testid="finance-estimate">
      <p className="text-xs font-extrabold uppercase tracking-[2px] text-black/50" style={head}>Financing estimate</p>
      <p className="mt-1 flex items-baseline gap-2"><span className="text-4xl font-extrabold" style={head} data-testid="finance-monthly">{money(monthly(financed(p), n))}</span><span className="text-black/50">/ month</span></p>
      <div className="mt-3 flex gap-2" data-testid="finance-terms">
        {TERMS.map((t) => <button key={t} onClick={() => setN(t)} data-testid={`finance-term-${t}`}
          className={`flex-1 rounded-full border py-1.5 text-sm font-bold transition-colors ${n === t ? "border-black bg-black text-white" : "border-black/15 hover:border-black"}`}>{t} mo</button>)}
      </div>
      <p className="mt-3 text-xs text-black/50" data-testid="finance-disclaimer">Estimate on {money(financed(p))} (machine + {money(p.public_shipping_usd || 0)} freight) at {APR}% APR for {n} months, no down payment. Not a loan offer. Actual rate and terms depend on credit approval by our financing partner.</p>
      <a href={LENDER_URL} target="_blank" rel="noopener noreferrer" className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-[#4CCC06] py-3 font-bold text-black transition-transform hover:-translate-y-0.5" data-testid="finance-apply">
        <ExternalLink size={16} />Apply for financing</a>
    </div>
  );
};

export const MachinesHero = () => (
  <section className="relative overflow-hidden bg-[#0B0B0B] text-white" data-testid="machines-hero">
    <div className="relative mx-auto grid max-w-6xl items-center gap-10 px-5 pb-14 pt-12 sm:px-8 lg:grid-cols-[1fr_1.3fr] lg:pb-20 lg:pt-16">
      <div>
        <p className="mb-4 text-xs font-bold uppercase tracking-[4px] text-[#4CCC06]" style={head}>Essential &amp; XR series</p>
        <h1 className="text-4xl font-extrabold uppercase leading-[0.95] sm:text-5xl lg:text-6xl" style={head} data-testid="machines-hero-title">Pulsed laser<br />cleaning machines.</h1>
        <p className="mt-6 max-w-md text-base text-white/70 md:text-lg">Professional 300W and 500W pulsed laser cleaners in two platforms, all on standard 110V power, plus the XR Industrial 1000W for the biggest jobs.</p>
      </div>
      <video className="aspect-video w-full rounded-2xl bg-black object-cover shadow-2xl ring-1 ring-white/10" controls playsInline preload="metadata"
        poster="/media/alr-laser-machines-poster.jpg" data-testid="machines-video">
        <source src="/media/alr-laser-machines.mp4" type="video/mp4" />
      </video>
    </div>
  </section>
);
